import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Plus, Trash2, Star, Save, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VARS = ["{primeiro_nome}", "{nome}", "{lider}", "{ministerio}", "{igreja}", "{devocional}"];

export const ConnectionTemplatesDialog = ({ open, onOpenChange, settings, onSaved }) => {
  const [templates, setTemplates] = useState([]);
  const [padraoId, setPadraoId] = useState("");
  const [nomeIgreja, setNomeIgreja] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && settings) {
      setTemplates(settings.templates || []);
      setPadraoId(settings.template_padrao_id || "");
      setNomeIgreja(settings.nome_igreja || "");
    }
  }, [open, settings]);

  const update = (idx, field, value) => setTemplates(prev => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));

  const save = async () => {
    if (templates.some(t => !t.nome.trim() || !t.texto.trim())) { toast.error("Preencha nome e texto de todos os modelos"); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/connections/settings`, { templates, template_padrao_id: padraoId, nome_igreja: nomeIgreja || null });
      toast.success("Modelos salvos");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6 [&>*]:min-w-0" data-testid="connection-templates-dialog">
        <DialogHeader>
          <DialogTitle>Modelos de mensagem de conexão</DialogTitle>
          <DialogDescription>Use as variáveis abaixo no texto; elas são substituídas automaticamente.</DialogDescription>
          <div className="flex flex-wrap gap-1">
            {VARS.map(v => <Badge key={v} variant="secondary" className="font-mono text-[10px]">{v}</Badge>)}
          </div>
          <p className="text-[11px] text-muted-foreground">
            <span className="font-mono">{"{devocional}"}</span> insere um resumo motivacional do devocional do dia, gerado por IA com o contexto bíblico.
          </p>
        </DialogHeader>

        <div className="space-y-1">
          <Label className="text-xs">Nome da igreja (usado em {"{igreja}"})</Label>
          <Input value={nomeIgreja} onChange={(e) => setNomeIgreja(e.target.value)} placeholder="Ex: Life Canedo" data-testid="templates-church-name" />
        </div>

        <div className="space-y-3">
          {templates.map((t, idx) => (
            <div key={t.id || idx} className={`p-3 rounded-xl border space-y-2 ${padraoId === t.id ? "border-primary/50 bg-primary/5" : ""}`} data-testid={`template-item-${idx}`}>
              <div className="flex items-center gap-2">
                <Input value={t.nome} onChange={(e) => update(idx, "nome", e.target.value)} placeholder="Nome do modelo" className="h-9" data-testid={`template-name-${idx}`} />
                <Button
                  type="button" variant={padraoId === t.id ? "default" : "outline"} size="icon" className="h-9 w-9 shrink-0"
                  title="Definir como padrão" onClick={() => setPadraoId(t.id)} data-testid={`template-default-${idx}`}
                >
                  <Star className={`h-4 w-4 ${padraoId === t.id ? "fill-current" : ""}`} />
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-destructive" onClick={() => setTemplates(prev => prev.filter((_, i) => i !== idx))} data-testid={`template-delete-${idx}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <Textarea value={t.texto} onChange={(e) => update(idx, "texto", e.target.value)} rows={3} className="text-sm" data-testid={`template-text-${idx}`} />
            </div>
          ))}
          <Button type="button" variant="outline" className="w-full" onClick={() => setTemplates(prev => [...prev, { id: `tpl-${Date.now()}`, nome: "", texto: "" }])} data-testid="template-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo modelo
          </Button>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} data-testid="templates-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectionTemplatesDialog;
